import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiHelpers, extractApiData, parseApiResponse } from '@/api/core';
import type {
  CreateAllowedEmailRequest,
  UpdateAllowedEmailRequest,
  UpdateRequestStatusRequest,
  AllowedEmail,
} from '@packages/types/admin/allowedEmail';

// Query Keys
const allowedEmailKeys = {
  all: ['allowedEmails'] as const,
  list: () => [...allowedEmailKeys.all, 'list'] as const,
  listWithParams: (includeUsed: boolean, page: number, limit: number) =>
    [...allowedEmailKeys.list(), { includeUsed, page, limit }] as const,
  detail: (id: number) => [...allowedEmailKeys.all, 'detail', id] as const,
};

/**
 * 가입 허용 이메일 목록 조회
 * 주기 폴링 없음 — CUD mutation 후 invalidate, 창 포커스 시 기본 refetch.
 */
export const useGetAllowedEmails = (
  includeUsed: boolean = false,
  page: number = 1,
  limit: number = 100
) => {
  return useQuery({
    queryKey: allowedEmailKeys.listWithParams(includeUsed, page, limit),
    queryFn: async () => {
      const response = await apiHelpers.get<AllowedEmail[]>(
        'allowedEmails.list',
        {},
        {
          includeUsed: includeUsed ? 'true' : 'false',
          page,
          limit,
        }
      );
      const parsed = parseApiResponse<AllowedEmail[]>(response);
      return {
        data: parsed.data ?? [],
        pagination: parsed.meta?.pagination ?? (response.data as any)?.pagination,
      };
    },
  });
};

/**
 * 가입 허용 이메일 추가
 */
export const useCreateAllowedEmail = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (dto: CreateAllowedEmailRequest) => {
      const response = await apiHelpers.post<AllowedEmail>('allowedEmails.create', dto);
      return extractApiData(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: allowedEmailKeys.list() });
    },
  });
};

/**
 * 가입 허용 이메일 수정
 */
export const useUpdateAllowedEmail = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (dto: UpdateAllowedEmailRequest & { id: number }) => {
      const response = await apiHelpers.patch<AllowedEmail>('allowedEmails.update', dto, {
        id: dto.id,
      });
      return extractApiData(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: allowedEmailKeys.list() });
    },
  });
};

/**
 * 가입 허용 이메일 삭제
 */
export const useDeleteAllowedEmail = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await apiHelpers.delete<void>('allowedEmails.delete', { id });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: allowedEmailKeys.list() });
    },
  });
};

/**
 * 요청 상태 업데이트 (승인/거부)
 */
export const useUpdateRequestStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (dto: UpdateRequestStatusRequest) => {
      const response = await apiHelpers.patch<AllowedEmail>(
        'allowedEmails.updateStatus',
        dto,
        { id: dto.id }
      );
      return extractApiData(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: allowedEmailKeys.list() });
    },
  });
};
